import React, { Fragment, useState } from 'react'
import {
    BlockWrapper,
    defineBlock,
    useBlockEffect,
    useEditor,
    smartProps,
    useInspectorControl,
} from '@smartfire/js-block-engine'
import style from './style.module.scss'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import apiFetch from '@wordpress/api-fetch'
import { menuItemParams, socialsParams } from '../../types'
import { Socials } from './Socials'
import { PromoteApp } from './PromoteApp'

const MenuMap = ({ arr }) => {
    if (!arr) return null

    return arr.children.map((el, i) => (
        <Fragment key={i}>
            {el.url !== '#container' && (
                <div key={i} className={style.titleOne}>
                    {el.url && el.url !== '#' ? (
                        <a href={el.url} target={el.target}>
                            {el.title}
                        </a>
                    ) : (
                        <div>{el.title}</div>
                    )}
                </div>
            )}
            {el.children.map((child, y) => (
                <Fragment key={y}>
                    <div key={y} className={style.titleTwo}>
                        {child.url && child.url !== '#' ? (
                            <a href={child.url} target={child.target} className={style.item}>
                                {child.title}
                            </a>
                        ) : (
                            <div>{child.title}</div>
                        )}
                    </div>
                    {child.children.map((subChild, k) => (
                        <div key={k} className={style.titleThree}>
                            <a href={subChild.url} target={subChild.target}>
                                {subChild.title}
                            </a>
                        </div>
                    ))}
                </Fragment>
            ))}
        </Fragment>
    ))
}

const InspectorLazy = React.lazy(() => import('./Inspector'))

type Props = {
    menu: menuItemParams[]
    maxHeightMobile: number
    maxHeightTablet: number
    maxHeightDesktop: number
    socials: socialsParams
    appLinks: { apple: string; google: string }
    isAppPromoted: boolean
}

const Menu: React.FC<Props> = ({ menu, socials, appLinks, isAppPromoted }) => {
    useInspectorControl(InspectorLazy, [])

    const { isEditing } = useEditor()
    const [menuCpy, setMenuCpy] = useState(menu)
    const [socialsCpy, setSocialsCpy] = useState(socials)
    const isTablet = useMediaQuery('(max-width: ' + style.tabletBreakpoint + ')')
    const isMobile = useMediaQuery('(max-width: ' + style.mobileBreakpoint + ')')

    useBlockEffect(() => {
        if (isEditing) {
            apiFetch({
                path: '/smartfire/menu',
                parse: false,
            })
                .then((res: any) => res.json())
                .then((data) => {
                    setMenuCpy(data)
                })
            apiFetch({
                path: '/smartfire/socials',
                parse: false,
            })
                .then((res: any) => res.json())
                .then((data) => {
                    setSocialsCpy(data)
                })
        }
    }, [isEditing])

    return (
        <BlockWrapper.Div className={style.menuContainer}>
            <div className={style.content}>
                {!isMobile && (
                    <div className={style.menu}>
                        {
                            //desktop
                            !isTablet &&
                                menuCpy?.length &&
                                menuCpy.map((group, x, { length }) => {
                                    return (
                                        <Fragment key={x}>
                                            <div className={style['column' + x]}>
                                                <MenuMap arr={group} />
                                                {x === length - 1 && (
                                                    <Fragment>
                                                        <Socials socials={socialsCpy} />
                                                        {isAppPromoted && appLinks && (
                                                            <PromoteApp
                                                                apple={appLinks.apple}
                                                                google={appLinks.google}
                                                            />
                                                        )}
                                                    </Fragment>
                                                )}
                                            </div>
                                            {(!isTablet || x === 1) && x < length - 1 && (
                                                <div className={style['line' + x]} />
                                            )}
                                        </Fragment>
                                    )
                                })
                        }
                        {
                            //tablet
                            isTablet && menuCpy?.length && (
                                <>
                                    <div className={style.column0}>
                                        <MenuMap arr={menuCpy[0]} />
                                        <MenuMap arr={menuCpy[1]} />
                                    </div>
                                    <div className={style.line1} />
                                    <div className={style.column1}>
                                        <MenuMap arr={menuCpy[2]} />
                                        <MenuMap arr={menuCpy[3]} />
                                        <Socials socials={socialsCpy} />
                                        {isAppPromoted && (
                                            <PromoteApp
                                                apple={appLinks.apple}
                                                google={appLinks.google}
                                            />
                                        )}
                                    </div>
                                </>
                            )
                        }
                    </div>
                )}
            </div>

            {isMobile && (
                <Fragment>
                    <Socials socials={socialsCpy} />
                    {isAppPromoted && (
                        <PromoteApp apple={appLinks.apple} google={appLinks.google} />
                    )}
                </Fragment>
            )}
        </BlockWrapper.Div>
    )
}

export default defineBlock({
    Block: Menu,
    smartProps: { isAppPromoted: smartProps.bool.withDefault(false) },
    setup: {
        title: 'Footer - Menu',
        parent: ['footer'],
    },
})
